import type { AlertEvent } from '@apm/contracts';
import { alertMessage } from './format';

/**
 * 브라우저 알림 (10회차 결정 5).
 *
 * **사용자가 켠 경우에만** 뜬다. 관제 화면을 열어 둔 사람에게 물어보지도 않고 알림 권한을
 * 요구하는 것은 무례하고, 대개 거절당한 뒤 다시는 못 켜게 된다.
 *
 * 켠 상태는 브라우저에만 둔다(`localStorage`). 서버가 알 이유가 없다.
 *
 * 23-A회차에 **권한 상태 안내**가 붙었다(UI 피드백 19). 브라우저에서 알림을 한 번 거부하면
 * 토글을 눌러도 켜지지 않고 아무 말도 없어서 "고장 났다"로 보인다. 지금 권한이 어떤지와
 * 거부됐으면 어디서 푸는지를 토글 옆에 적는다.
 */

const STORAGE_KEY = 'apm.alerts.desktop';

/** 브라우저가 알림을 지원하는가. 테스트(node)와 옛 브라우저에서는 없다 */
export function isSupported(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window;
}

/**
 * 지금 이 브라우저의 알림 권한 (23-A회차, UI 피드백 19).
 * `unsupported`는 브라우저가 알림 자체를 모르는 경우다
 */
export type NotifyPermission = 'granted' | 'denied' | 'default' | 'unsupported';

export function permissionOf(): NotifyPermission {
  if (!isSupported()) return 'unsupported';
  const value = Notification.permission;
  return value === 'granted' || value === 'denied' ? value : 'default';
}

/**
 * 권한 상태 → 토글 옆에 적을 말. 거부됐으면 푸는 길까지 적는다.
 * `granted`는 아무 말도 하지 않는다 — 잘 되는 것에 설명을 붙이면 문제처럼 보인다
 */
export function permissionNote(permission: NotifyPermission, enabled: boolean): string {
  switch (permission) {
    case 'denied':
      return (
        '이 브라우저에서 알림이 차단돼 있다.\n' +
        '주소창 자물쇠 → 사이트 설정 → 알림을 허용으로 바꾼 뒤 화면을 새로고침할 것'
      );
    case 'default':
      return '켜면 브라우저가 허용을 묻는다';
    case 'unsupported':
      return '이 브라우저는 알림을 지원하지 않는다';
    default:
      return enabled ? '' : '허용돼 있다. 켜면 바로 뜬다';
  }
}

export function isEnabled(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    // 사생활 보호 모드 등에서 접근이 막힌다. 그때는 꺼진 것으로 본다
    return false;
  }
}

/** 켤 때만 권한을 묻는다. 권한을 못 받으면 켜지 않는다 */
export async function setEnabled(enabled: boolean): Promise<boolean> {
  if (!enabled) {
    write('0');
    return false;
  }
  if (!isSupported()) return false;
  const permission =
    Notification.permission === 'default'
      ? await Notification.requestPermission()
      : Notification.permission;
  if (permission !== 'granted') {
    write('0');
    return false;
  }
  write('1');
  return true;
}

function write(value: string): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // 저장을 못 해도 이번 세션 동안은 화면 상태로 동작한다
  }
}

/**
 * 이 알림을 띄울까.
 *
 * 복구는 띄우지 않는다. 화면을 안 보고 있는 사람에게 필요한 것은 "터졌다"이지
 * "돌아왔다"가 아니다. 돌아온 것은 화면에서 확인하면 된다.
 */
export function shouldNotify(event: AlertEvent | undefined, enabled: boolean): boolean {
  if (!enabled || event === undefined || !event.notified || event.severity !== 'critical') {
    return false;
  }
  // 일반 경고·프로파일·재기동은 이력에서 확인한다. 사라짐·서버 오류는 활성 목록이 없는 통보다.
  return (
    (event.state === 'firing' && event.endedAt === undefined) ||
    (event.state === 'notice' &&
      (event.signal === 'instance_gone' || event.signal === 'server_error'))
  );
}

/** 제목·본문. 본문은 알림 문구이되 신호 이름은 화면 말로 바꾼다 (44회차) */
export function notificationOf(event: AlertEvent): { title: string; body: string } {
  return { title: `${event.ruleName} — ${event.key}`, body: alertMessage(event) };
}

/**
 * 띄운 알림. 로그아웃하면 닫는다 (51회차 리뷰 R51-B-3). 알림 센터에 남는 창에는 앞사람이 본
 * 경보 제목·본문이 실려 있다. 사용자가 닫은 알림은 `close` 이벤트로 빠진다
 */
const open = new Set<Notification>();

/** 실제로 띄운다. 실패는 삼킨다. 알림 하나 때문에 화면이 멈추면 안 된다 */
export function showNotification(event: AlertEvent): void {
  if (!isSupported() || Notification.permission !== 'granted') return;
  const { title, body } = notificationOf(event);
  try {
    // tag를 알림 id로 두면 같은 알림이 여러 번 쌓이지 않고 덮어쓴다
    const shown = new Notification(title, { body, tag: event.id });
    open.add(shown);
    shown.addEventListener?.('close', () => open.delete(shown));
  } catch {
    // 일부 브라우저는 서비스 워커 없이 생성자를 막는다
  }
}

/** 띄운 알림을 모두 닫는다. 로그아웃·세션 만료 때 부른다 */
export function closeNotifications(): void {
  for (const shown of open) {
    try {
      shown.close();
    } catch {
      // 이미 닫힌 것이다
    }
  }
  open.clear();
}
